import React from 'react'
import { Lockup, TextVerticalSpacer, VerticalSpacer, Button, Aside } from '@superhi/design'
import { useRouter } from 'next/router'

import { API } from '../../../../api'
import Base from '../Base'
import Share from '../Base/components/Share'
import LibraryPostVideoCard from '../../../../components/LibraryPostVideoCard'
import { useFullPageUrl } from '../../../../components/utils'

import DifficultyLevel from './components/DifficultyLevel'
import Transcript from './components/Transcript'
import VideoPlayer from './components/VideoPlayer'

const toNumber = (time: string | string[]) =>
  time ? parseInt(Array.isArray(time) ? time[0] : time) : undefined

type Props = Omit<React.ComponentProps<typeof Base>, 'header' | 'imageUrl' | 'type'> & {
  wistiaId: string
  authorName: string
  authorUrl?: string
  next?: React.ComponentProps<typeof LibraryPostVideoCard>
  previous?: React.ComponentProps<typeof LibraryPostVideoCard>
  demoUrl?: string
  difficultyLevel: API.VideoDifficultyLevel
  transcript: {
    englishName: string
    language: string
    nativeName: string
    text: string
  }[]
  image: { url: string; width: number; height: number }
}

const LibraryPostVideo: React.FC<Props> = ({
  title,
  authorName,
  authorUrl,
  next,
  previous,
  difficultyLevel,
  wistiaId,
  transcript = [],
  demoUrl,
  image,
  ...props
}) => {
  const router = useRouter()
  const metaItems = React.useMemo(
    () => [
      {
        title: 'Difficulty',
        content: <DifficultyLevel difficultyLevel={difficultyLevel} />,
      },
      {
        title: 'Posted by',
        content: authorName,
        href: authorUrl,
        isExternal: true,
      },
    ],
    [authorName, authorUrl, difficultyLevel],
  )

  const fullPageUrl = useFullPageUrl()

  return (
    <Base
      {...props}
      type="video"
      title={title}
      imageUrl={image.url}
      metaItems={metaItems}
      header={<VideoPlayer id={wistiaId} time={toNumber(router.query.t ?? '')} />}
    >
      {demoUrl && (
        <Button href={demoUrl} isExternal>
          View demo
        </Button>
      )}

      <Aside label="Share this post">
        <Share title={title} url={fullPageUrl} />
      </Aside>

      {transcript.length > 0 && (
        <TextVerticalSpacer level="2">
          <Lockup title="Transcript" align="left" level="2" titleColor="GREY_90" />

          <Transcript>{transcript[0].text}</Transcript>
        </TextVerticalSpacer>
      )}
    </Base>
  )
}

export default LibraryPostVideo
